<template>
  <div
    class="signup-page container"
    data-test="page-signup" >
    <h4 class="t-c m-b font-normal">Create an account</h4>
    <el-form
      ref="user"
      :model="user"
      :rules="rules"
      :status-icon="true"
      class="overflow-icon"
      @keyup.enter.native="onSubmit">
      <el-form-item
        prop="email"
        label="Email Address">
        <el-input
          v-model="user.email"
          placeholder="user@email.com"
          data-test="input-email"/>
      </el-form-item>
      <el-form-item
        prop="password"
        label="Create a password">
        <el-input
          v-model="user.password"
          :type="show_pass ? 'text' : 'password'"
          placeholder="8+ characters, including 1 number"
          data-test="input-password"
          class="password"
          @keyup.native="checkValidation"/>
        <show-password @show="(e) => {show_pass = e}"/>
      </el-form-item>
      <el-form-item>
        <el-row>
          <el-col :span="2">
            <el-checkbox
              v-model="user.marketing_consent"
              data-test="checkbox"/>
          </el-col>
          <el-col :span="22">
            <p>By checking this box you opt in to receive regular updates, news and other emails marketing about Luminary.</p>
          </el-col>
        </el-row>
      </el-form-item>
      <div class="consent">
        <p>By clicking below you agree to our <br>
          <router-link
            target="_blank"
            to="/terms"
            data-test="link-termservice"
          >
            Terms of Service
          </router-link>and
          <router-link
            to="/privacy"
            target="_blank"
            data-test="link-privacypolicy"
          >
            Privacy Policy
          </router-link>
        </p>
      </div>
      <div class="t-c action-buttons">
        <el-button
          v-loading="loading"
          :disabled="!form_valid || loading"
          type="primary"
          class="large"
          data-test="btn-signup"
          @click="onSubmit">Sign Up</el-button>
      </div>
      <div class="t-c m-t">
        <p>Already have an account? &nbsp;<router-link
          to="/signin"
          data-test="link-login">Log In</router-link></p>
      </div>
    </el-form>
  </div>
</template>
<script>
import User from '../../services/User';
import PageMixin from '../../shared/pageMixin';
import ShowPassword from '../../components/ShowPassword';

export default {
  name: 'RegisterPage',
  components: {
    'show-password': ShowPassword,
  },
  mixins: [PageMixin],
  data() {
    const passwordStrength = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('Password Required'));
      } else {
        const strongRegex = new RegExp('^(?=.*[a-zA-Z])(?=.*[0-9])(?=.{8,})');

        if (strongRegex.test(value)) {
          callback();
        } else {
          callback(new Error('Weak Password Strength (8+ characters, including 1 number)'));
        }
      }
    };
    return {
      pageName: 'Create Account',
      userProvider: null,
      user: {
        email: '',
        password: '',
        marketing_consent: true,
      },
      rules: {
        email: [{ required: true, message: 'Email Required', trigger: 'blur' }, { type: 'email', message: 'Invalid Email Format', trigger: 'blur' }],
        password: [{ required: true, validator: passwordStrength, trigger: 'blur' }],
      },
      loading: false,
      form_valid: false,
      show_pass: false,
      consent_msg: 'By checking this box you opt in to receive regular updates, \n news and other email marketing about Luminary.',
      fingerprint_id: '',
    };
  },
  mounted() {
    this.userProvider = new User();
    // window.eventBus.$emit('onboarding-step-change', 0);
    this.fingerprint_id = localStorage.getItem('branch_fingerprint_id');
    if (!this.fingerprint_id) {
      this.getBrowsersFingerPrintId();
    }
  },
  methods: {
    onSubmit() {
      if (this.loading) return;
      const ctx = this;
      this.$refs.user.validate((valid) => {
        if (valid) {
          ctx.registerUser();
        }
      });
    },
    registerUser() {
      this.loading = true;
      this.user.username = this.user.email;
      this.userProvider.register(this.user).then(() => {
        this.$store.commit('setUser', this.user);
        this.$store.commit('setSignupCompleted', true);
        this.registerToLuminary(this.user.marketing_consent);
      }).catch((error) => {
        this.loading = false;
        this.showErrorMsg(error);
      });
    },
    registerToLuminary(mktConsent) {
      // const branchData = localStorage.getItem('branch_data');
      this.userProvider.registerToLuminary(mktConsent, this.fingerprint_id)
        .then((resp) => {
          this.loading = false;
          // localStorage.removeItem('branch_data');
          this.showSuccessMsg('Your account has been created!');

          this.$analytics.identify(resp.user.uuid);
          if (this.$route && this.$route.query && this.$route.query.redirect) {
            this.$router.push(this.$route.query.redirect);
          } else {
            this.$router.push('/user-categories');
          }
          this.$analytics.track('ga_account_created');
        })
        .catch((error) => {
          this.loading = false;
          this.showErrorMsg(error);
        });
    },
    checkValidation() {
      if (this.user.email.length && this.user.password.length > 7) {
        this.$refs.user.validate((valid) => { this.form_valid = valid; });
      }
    },
    getBrowsersFingerPrintId() {
      if (!window.branch || (window.branch && !window.branch.getBrowserFingerprintId)) {
        return;
      }
      window.branch.getBrowserFingerprintId((err, data) => {
        if (data) {
          localStorage.setItem('branch_fingerprint_id', data);
          this.fingerprint_id = data;
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/scss/variables.scss";
.signup-page {
  margin: 72px auto;
  h4 {
    font-family: $clearface-bold;
    color: $black;
    font-size: 40px;
    letter-spacing: -1px;
    line-height: 44px;
    margin: 46px 0 37px 0;
  }
  .el-checkbox__input.is-checked + .el-checkbox__label {
    color: $black !important;
  }
  .el-form-item__error {
    color: $gray;
  }
  .el-checkbox__inner {
    width: 18px;
    height: 18px;

    &::after {
      width: 6px;
      height: 8px;
    }
  }
  .consent {
    margin-top: 10px;
    text-align: center;
  }
  a {
    color:$black;
    text-decoration: underline;
  }
  .action-buttons {
    margin-top: 24px;
    margin-bottom: 24px;
  }
}
</style>



// WEBPACK FOOTER //
// app/src/pages/onboarding/SignUp.vue